/**
 * alertsApi.js — API functions for the V2 Alerts page.
 *
 * fetchMaintenanceAlerts  — polls maintenance alarms (batteries + other)
 * fetchLostDevices        — polls offline devices
 * clearMaintenanceAlert   — marks a maintenance alarm as cleared/deleted
 */

import { apiFetch } from '../../../../services/api.js';
import { getAuthHeaders } from '../../../../services/auth';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Fetch Maintenance Alerts (batteries + other)
// ---------------------------------------------------------------------------

/**
 * @param {string} campusGuid — campus GUID
 * @param {string} userGuid — user GUID
 * @param {boolean} migrated
 * @param {string} mode — "station" | "standard"
 * @param {object} flags — { hybrid, legacy, new: show_new }
 * @returns {Promise<Array>} — processed alarm array
 */
export async function fetchMaintenanceAlerts(campusGuid, userGuid, migrated, mode = 'standard', flags = {}) {
    const body = {
        CampusId: campusGuid,
        ToGet: 'maint',
        UserId: userGuid,
        Migrated: migrated,
        Mode: mode,
        Hybrid: flags.hybrid,
        Legacy: flags.legacy,
        New: flags.new,
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_ALARMS_ENDPOINT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(body),
        });
        const raw = await resp.json();
        return processAlerts(raw);
    } catch (err) {
        console.error('[alertsApi] fetchMaintenanceAlerts error:', err);
        return [];
    }
}

/**
 * Process raw alert response into flat array (matches V1 ProcessAlerts pattern).
 */
function processAlerts(resp) {
    if (resp?.Status !== 'OK' || !resp?.Alarm) return [];
    return resp.Alarm.map((a) => ({
        AlarmId: a.AlarmId,
        EventDateTime: a.EventDateTime,
        AcceptUsername: a.AcceptDateTime ? a.AcceptUsername : '',
        AcceptDateTime: a.AcceptDateTime,
        AckDateTime: a.AckDateTime,
        Status: a.Status,
        AlertType: a.AlertType,
        AlertSubType: a.AlertSubType,
        DeviceUse: a.DeviceUse,
        DeviceId: a.DeviceId,
        SerialNumber: a.SerialNumber,
        LocationName: a.LocationName,
        LocationArea: a.LocationArea,
        LocationId: a.LocationId,
        NearName: a.NearName,
        ResidentsObject: a.ResidentsObject,
        GroupIds: a.GroupIds,
        MacAddr: a.MacAddr,
    }));
}

// ---------------------------------------------------------------------------
// Fetch Lost Devices (communication lost)
// ---------------------------------------------------------------------------

export function fetchLostDevices(campusId) {
    return apiFetch({
        procedure: 'sp_getDeviceOffline',
        reqType: 'stored',
        payload: { device_filter: '', campus_id: campusId },
        readOnly: true,
    });
}

// ---------------------------------------------------------------------------
// Clear Maintenance Alert
// ---------------------------------------------------------------------------

/**
 * @param {string} campusGuid
 * @param {string} alarmId
 * @param {string} username
 * @returns {Promise<[boolean, string?]>}
 */
export async function clearMaintenanceAlert(campusGuid, alarmId, username) {
    const now = new Date().toISOString().replace('T', ' ').split('.')[0];
    const ttl = Math.floor(Date.now() / 1000) + 1800; // 30 min TTL

    const body = {
        Table: 'Telemetry',
        PartitionKey: 'CampusId',
        SortKey: 'AlarmId',
        Content: [{
            CampusId: campusGuid,
            AlarmId: alarmId,
            DateModified: now,
            Deleted: true,
            AckDateTime: now,
            ModifiedBy: username,
            TTL: ttl,
        }],
        CampusId: campusGuid,
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_POST_ENDPOINT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(body),
        });
        const raw = await resp.json();
        if (raw?.status === 'success' || raw?.Status === 'OK') return [true];
        return [false, 'Clear failed'];
    } catch (err) {
        console.error('[alertsApi] clearMaintenanceAlert error:', err);
        return [false, 'Request failed'];
    }
}
